// Use backend proxy for caching (DynamoDB)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface JustTCGPrice {
  condition: string; // e.g., "Near Mint", "Lightly Played"
  printing?: string; // e.g., "Holofoil", "Normal", "Reverse Holofoil"
  price: number;
  currency: string;
  lastUpdated: string;
}

export interface JustTCGCardData {
  cardId: string;
  cardName: string;
  setName: string;
  prices: JustTCGPrice[];
  imageUrl?: string;
  marketPrice?: number;
  lowPrice?: number;
  highPrice?: number;

  // Market trends
  priceChange7d?: number;
  priceChange30d?: number;
  priceChange90d?: number;

  // Price ranges
  minPrice7d?: number;
  maxPrice7d?: number;
  minPrice30d?: number;
  maxPrice30d?: number;
  minPrice90d?: number;
  maxPrice90d?: number;

  // Volatility metrics
  stddevPrice7d?: number;
  trendSlope7d?: number;
  trendSlope30d?: number;

  // Price history for charts
  priceHistory7d?: Array<{ price: number; timestamp: number }>;
  priceHistory30d?: Array<{ price: number; timestamp: number }>;
  priceHistory90d?: Array<{ price: number; timestamp: number }>;
  priceHistory180d?: Array<{ price: number; timestamp: number }>;
}

/**
 * Fetch pricing data from JustTCG API for a card (One Piece or generic)
 */
export async function fetchMarketPricing(
  cardNumber: string,
  cardName: string,
  setId: string,
  rarity?: string,
  searchQuery?: string,
  justtcgId?: string,
): Promise<JustTCGCardData | null> {
  // Route based on game type:
  //   Pokemon  → TCGdex API (/api/market-prices)
  //   One Piece → JustTCG API (/api/card-pricing)
  const isOnePiece = /^(OP|EB|ST|PRB)/i.test(setId);

  if (isOnePiece) {
    return fetchFromJustTCG(cardNumber, cardName, setId, rarity, searchQuery, justtcgId);
  } else {
    return fetchFromTCGdex(cardNumber, setId);
  }
}

/**
 * Fetch pricing from TCGdex API (for Pokemon cards).
 * Calls /api/market-prices/card/:cardId and normalizes to JustTCGCardData shape.
 */
async function fetchFromTCGdex(
  cardNumber: string,
  setId: string,
): Promise<JustTCGCardData | null> {
  try {
    const cardId = `${setId}-${cardNumber}`;
    console.log('[TCGdex] Fetching pricing for:', cardId);

    const url = `${API_BASE_URL}/api/market-prices/card/${encodeURIComponent(cardId)}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.status === 404) {
        console.log('[TCGdex] No pricing found for:', cardId);
        return null;
      }

      if (!response.ok) {
        console.error('[TCGdex] API error:', response.status);
        return null;
      }

      const data = await response.json();
      const pricing = data.pricing;
      if (!pricing) return null;

      // Determine the best market price — prefer TCGplayer USD, then Cardmarket EUR
      let marketPrice: number | undefined;
      const tp = pricing.tcgplayer;
      const cm = pricing.cardmarket;

      if (tp?.available) {
        const variant = tp.holo || tp.normal || tp.reverse;
        marketPrice = variant?.marketPrice ?? variant?.midPrice ?? undefined;
      }
      if (marketPrice == null && cm?.available) {
        marketPrice = cm.trend ?? cm.avg ?? cm.low ?? undefined;
      }

      // Build condition/variant price rows for the table
      const prices: JustTCGPrice[] = [];
      if (tp?.available) {
        if (tp.normal) {
          prices.push({
            condition: 'Near Mint',
            printing: 'Normal',
            price: tp.normal.marketPrice ?? tp.normal.midPrice ?? 0,
            currency: 'USD',
            lastUpdated: tp.updated || new Date().toISOString(),
          });
        }
        if (tp.holo) {
          prices.push({
            condition: 'Near Mint',
            printing: 'Holofoil',
            price: tp.holo.marketPrice ?? tp.holo.midPrice ?? 0,
            currency: 'USD',
            lastUpdated: tp.updated || new Date().toISOString(),
          });
        }
        if (tp.reverse) {
          prices.push({
            condition: 'Near Mint',
            printing: 'Reverse Holofoil',
            price: tp.reverse.marketPrice ?? tp.reverse.midPrice ?? 0,
            currency: 'USD',
            lastUpdated: tp.updated || new Date().toISOString(),
          });
        }
      }
      if (cm?.available && prices.length === 0) {
        // Fallback to Cardmarket if no TCGplayer data
        prices.push({
          condition: 'Near Mint',
          printing: 'Standard',
          price: cm.trend ?? cm.avg ?? 0,
          currency: 'EUR',
          lastUpdated: cm.updated || new Date().toISOString(),
        });
        if (cm.trendHolo != null && cm.trendHolo > 0) {
          prices.push({
            condition: 'Near Mint',
            printing: 'Holofoil',
            price: cm.trendHolo,
            currency: 'EUR',
            lastUpdated: cm.updated || new Date().toISOString(),
          });
        }
      }

      const result: JustTCGCardData = {
        cardId: pricing.cardId,
        cardName: pricing.cardName,
        setName: pricing.setName,
        prices,
        imageUrl: pricing.imageUrl,
        marketPrice,
        lowPrice: tp?.available
          ? (tp.holo?.lowPrice ?? tp.normal?.lowPrice ?? tp.reverse?.lowPrice ?? undefined)
          : (cm?.low ?? undefined),
        highPrice: tp?.available
          ? (tp.holo?.highPrice ?? tp.normal?.highPrice ?? tp.reverse?.highPrice ?? undefined)
          : undefined,
      };

      // Cardmarket trend data for change display
      if (cm?.available) {
        // Calculate approximate changes from Cardmarket averages
        if (cm.avg7 != null && cm.avg30 != null) {
          const currentAvg = cm.avg ?? cm.trend ?? 0;
          if (cm.avg7 > 0) result.minPrice7d = cm.avg7;
          if (cm.avg30 > 0) result.minPrice30d = cm.avg30;
        }
      }

      console.log('[TCGdex] Normalized pricing:', {
        marketPrice: result.marketPrice,
        currency: pricing.currency,
        variants: prices.length,
      });

      return result;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('[TCGdex] Request timeout');
    } else {
      console.error('[TCGdex] Error fetching pricing:', error);
    }
    return null;
  }
}

/**
 * Fetch pricing from JustTCG API (for One Piece cards).
 * Calls /api/card-pricing/pricing/:setId/:cardNumber/:cardName
 */
async function fetchFromJustTCG(
  cardNumber: string,
  cardName: string,
  setId: string,
  rarity?: string,
  searchQuery?: string,
  justtcgId?: string,
): Promise<JustTCGCardData | null> {
  try {
    console.log('[JustTCG] Fetching pricing from backend proxy:', {
      cardNumber,
      cardName,
      setId,
    });

    const safeCardName = cardName || '_';
    let url = `${API_BASE_URL}/api/card-pricing/${encodeURIComponent(setId)}/${encodeURIComponent(cardNumber)}/${encodeURIComponent(safeCardName)}`;

    const params = new URLSearchParams();
    params.append('game', 'onepiece');
    if (searchQuery) params.append('searchQuery', searchQuery);
    if (justtcgId) params.append('justtcgId', justtcgId);
    
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.status === 404) return null;
      if (!response.ok) {
        console.error('[JustTCG] Backend proxy error:', response.status);
        return null;
      }

      const data = await response.json();
      if (!data?.pricing) return null;
      return data.pricing;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('[JustTCG] Request timeout');
    } else {
      console.error('[JustTCG] Error fetching pricing:', error);
    }
    return null;
  }
}

/**
 * Fetch pricing for multiple cards in batch
 */
export async function fetchBatchOnePiecePricing(
  cards: Array<{
    cardNumber: string;
    cardName: string;
    setId: string;
    rarity?: string;
  }>,
): Promise<Map<string, JustTCGCardData>> {
  const results = new Map<string, JustTCGCardData>();

  // JustTCG free tier allows 20 cards per request
  const batchSize = 20;

  for (let i = 0; i < cards.length; i += batchSize) {
    const batch = cards.slice(i, i + batchSize);
    const promises = batch.map((card) =>
      fetchMarketPricing(
        card.cardNumber,
        card.cardName,
        card.setId,
        card.rarity,
      ),
    );

    const batchResults = await Promise.allSettled(promises);

    batchResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        results.set(batch[index].cardNumber, result.value);
      }
    });

    // Rate limiting - wait 1 second between batches
    if (i + batchSize < cards.length) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  return results;
}

/**
 * Fetch pricing from JustTCG v2 API (specifically for Pokemon cards).
 * Calls /api/card-pricing/v2/:setId/:cardNumber/:cardName
 */
export async function fetchFromJustTCGv2(
  cardNumber: string,
  cardName: string,
  setId: string,
  searchQuery?: string,
  justtcgId?: string,
  cache?: string,
): Promise<JustTCGCardData | null> {
  try {
    console.log('[JustTCG v2] Fetching pricing from backend proxy v2:', {
      cardNumber,
      cardName,
      setId,
      cache,
    });

    const safeCardName = cardName || '_';
    let url = `${API_BASE_URL}/api/v2/card-pricing/${encodeURIComponent(setId)}/${encodeURIComponent(cardNumber)}/${encodeURIComponent(safeCardName)}`;

    const params = new URLSearchParams();
    params.append('game', 'pokemon');
    if (searchQuery) params.append('searchQuery', searchQuery);
    if (justtcgId) params.append('justtcgId', justtcgId);
    if (cache) params.append('cache', cache);
    
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (response.status === 404) {
        console.log('[JustTCG v2] No pricing found');
        return null;
      }

      if (!response.ok) {
        console.error('[JustTCG v2] API error:', response.status);
        return null;
      }

      const data = await response.json();
      return data.pricing || null;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      throw fetchError;
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('[JustTCG v2] Request timeout');
    } else {
      console.error('[JustTCG v2] Error fetching pricing:', error);
    }
    return null;
  }
}

